import {
  classifyAnalyticsError,
  stripUndefinedValues,
  type AnalyticsErrorCategory,
} from './analytics.js';

export type AppErrorSurface =
  | 'framer_connection_test'
  | 'source_preview'
  | 'field_creation'
  | 'field_type_update'
  | 'config_save'
  | 'sync_launch'
  | 'sync_execution'
  | 'react_query'
  | 'unknown';

export type AppErrorContext = {
  activation_milestone?: string | null;
  array_candidate_path_count?: number;
  candidate_path_count?: number;
  code?: string;
  collection_name?: string | null;
  config_id?: string | null;
  diagnostic_code?: string;
  error_category?: AnalyticsErrorCategory;
  field_id?: string | null;
  created_field_count?: number;
  failed_field_count?: number;
  failure_phase?: string;
  has_post_body?: boolean;
  http_status?: number;
  is_retryable?: boolean;
  missing_field_count?: number;
  missing_field_names?: string[];
  network_error_message?: string;
  network_error_name?: string;
  plan?: string | null;
  project_id?: string | null;
  provider?: string | null;
  preview_field_count?: number;
  preview_record_count?: number;
  request_duration_ms?: number;
  request_id?: string | null;
  request_method?: string;
  request_path?: string;
  request_query_keys?: string[];
  request_url?: string;
  resolved_items_path?: string | null;
  response_body_kind?: string;
  response_body_preview?: string;
  response_content_type?: string | null;
  response_shape?: string | null;
  requested_field_count?: number;
  run_id?: string | null;
  source_kind?: string | null;
  step: string;
  surface: AppErrorSurface;
  trigger_source?: string | null;
  updated_field_type_count?: number;
  user_visible_message?: string;
};

export type AppErrorReport = AppErrorContext & {
  details?: unknown;
  distinct_id?: string | null;
  exception_message?: string;
};

export type AppErrorEnvelope = {
  code: string;
  diagnostic?: Record<string, unknown>;
  details?: string;
  message: string;
  requestId: string;
  retryable?: boolean;
};

const SENSITIVE_KEY_PATTERN =
  /(authorization|cookie|password|secret|token|api[_-]?key|credential|signature)/i;
const HTML_TAG_PATTERN = /<!doctype html|<html[\s>]|<body[\s>]|<[a-z][\s\S]*>/i;
const SENSITIVE_STRING_PATTERN =
  /(?:bearer\s+[a-z0-9._-]{8,}|https?:\/\/[^/\s:@]+:[^@\s]+@|(?:access[_-]?token|refresh[_-]?token|api[_-]?key|client[_-]?secret|password|signature|secret)=[^&\s]+|(?:authorization|cookie)\s*:\s*\S+)/i;

function redactSensitiveString(value: string): string {
  if (SENSITIVE_STRING_PATTERN.test(value)) {
    return '[redacted]';
  }

  if (/bearer\s+[a-z0-9._-]+/i.test(value)) {
    return '[redacted]';
  }

  if (value.length > 80 && /[a-z0-9]{24,}/i.test(value)) {
    return '[redacted]';
  }

  return value;
}

function sanitizeErrorDetailInternal(value: unknown, depth: number): unknown {
  if (value == null || depth > 4) {
    return value ?? null;
  }

  if (value instanceof Error) {
    return {
      message: redactSensitiveString(value.message),
      name: value.name,
    };
  }

  if (typeof value === 'string') {
    return redactSensitiveString(value);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((entry) => sanitizeErrorDetailInternal(entry, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        SENSITIVE_KEY_PATTERN.test(key)
          ? '[redacted]'
          : sanitizeErrorDetailInternal(entry, depth + 1),
      ]),
    );
  }

  return value;
}

export function sanitizeErrorDetail(value: unknown): unknown {
  return sanitizeErrorDetailInternal(value, 0);
}

export function classifyAppErrorCategory(args: {
  code?: string;
  message?: string;
}): AnalyticsErrorCategory {
  const normalizedCode = args.code?.trim().toLowerCase() ?? '';

  if (!normalizedCode) {
    return classifyAnalyticsError(args.message ?? '');
  }

  if (normalizedCode.includes('auth') || normalizedCode.includes('credential')) {
    return 'auth';
  }
  if (normalizedCode.includes('permission') || normalizedCode.includes('forbidden')) {
    return 'permission';
  }
  if (normalizedCode.includes('validation')) {
    return 'validation';
  }
  if (normalizedCode.includes('timeout')) {
    return 'timeout';
  }
  if (normalizedCode.includes('network')) {
    return 'network';
  }
  if (normalizedCode.includes('not_found')) {
    return 'not_found';
  }
  if (normalizedCode.includes('conflict')) {
    return 'conflict';
  }
  if (normalizedCode.includes('rate_limit')) {
    return 'rate_limited';
  }
  if (
    normalizedCode.includes('framer') ||
    normalizedCode.includes('provider') ||
    normalizedCode.includes('airtable') ||
    normalizedCode.includes('notion') ||
    normalizedCode.includes('google')
  ) {
    return 'provider';
  }

  return classifyAnalyticsError(args.message ?? '');
}

function normalizeAppErrorCodePart(value: string | undefined): string | undefined {
  const normalized = value
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || undefined;
}

export function resolveAppErrorCode(
  input: Pick<AppErrorContext, 'code' | 'surface' | 'step'>,
): string {
  const explicitCode = normalizeAppErrorCodePart(input.code);
  if (explicitCode) {
    return explicitCode;
  }

  return [
    normalizeAppErrorCodePart(input.surface) ?? 'unknown',
    normalizeAppErrorCodePart(input.step) ?? 'unknown',
    'failed',
  ].join('_');
}

export function normalizeAppErrorReport(input: AppErrorReport): Record<string, unknown> {
  const code = resolveAppErrorCode(input);

  return stripUndefinedValues({
    activation_milestone: input.activation_milestone ?? undefined,
    array_candidate_path_count: input.array_candidate_path_count,
    candidate_path_count: input.candidate_path_count,
    code,
    collection_name: input.collection_name ?? undefined,
    config_id: input.config_id ?? undefined,
    diagnostic_code: input.diagnostic_code,
    details: sanitizeErrorDetail(input.details),
    error_category:
      input.error_category ??
      classifyAppErrorCategory({
        code,
        message: input.exception_message ?? input.user_visible_message,
      }),
    exception_message: input.exception_message,
    created_field_count: input.created_field_count,
    failed_field_count: input.failed_field_count,
    failure_phase: input.failure_phase,
    field_id: input.field_id ?? undefined,
    has_post_body: input.has_post_body,
    http_status: input.http_status,
    is_retryable: input.is_retryable,
    missing_field_count: input.missing_field_count,
    missing_field_names: input.missing_field_names,
    network_error_message:
      input.network_error_message === undefined
        ? undefined
        : sanitizeErrorDetail(input.network_error_message),
    network_error_name: input.network_error_name,
    plan: input.plan ?? undefined,
    project_id: input.project_id ?? undefined,
    provider: input.provider ?? undefined,
    preview_field_count: input.preview_field_count,
    preview_record_count: input.preview_record_count,
    request_duration_ms: input.request_duration_ms,
    request_id: input.request_id ?? undefined,
    request_method: input.request_method,
    request_path: input.request_path,
    request_query_keys: input.request_query_keys,
    request_url: input.request_url,
    resolved_items_path: input.resolved_items_path ?? undefined,
    response_body_kind: input.response_body_kind,
    response_body_preview:
      input.response_body_preview === undefined
        ? undefined
        : sanitizeErrorDetail(input.response_body_preview),
    response_content_type: input.response_content_type ?? undefined,
    response_shape: input.response_shape ?? undefined,
    requested_field_count: input.requested_field_count,
    run_id: input.run_id ?? undefined,
    source_kind: input.source_kind ?? undefined,
    step: input.step,
    surface: input.surface,
    trigger_source: input.trigger_source ?? undefined,
    updated_field_type_count: input.updated_field_type_count,
    user_visible_message: input.user_visible_message,
  });
}

export function extractAppErrorEnvelope(payload: unknown): AppErrorEnvelope | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const candidate = payload as {
    code?: unknown;
    details?: unknown;
    diagnostic?: unknown;
    message?: unknown;
    requestId?: unknown;
    retryable?: unknown;
  };

  if (
    typeof candidate.code !== 'string' ||
    typeof candidate.message !== 'string' ||
    typeof candidate.requestId !== 'string'
  ) {
    return null;
  }

  return {
    code: candidate.code,
    diagnostic:
      candidate.diagnostic && typeof candidate.diagnostic === 'object'
        ? (candidate.diagnostic as Record<string, unknown>)
        : undefined,
    message: candidate.message,
    requestId: candidate.requestId,
    details: typeof candidate.details === 'string' ? candidate.details : undefined,
    retryable: typeof candidate.retryable === 'boolean' ? candidate.retryable : undefined,
  };
}

export function isUnsafeUserVisibleMessage(
  message: string,
  options: {
    maxLength?: number;
  } = {},
): boolean {
  const trimmed = message.trim();
  if (!trimmed) {
    return true;
  }

  const maxLength = options.maxLength ?? 160;
  return (
    trimmed.length > maxLength ||
    HTML_TAG_PATTERN.test(trimmed) ||
    SENSITIVE_STRING_PATTERN.test(trimmed)
  );
}

export function getSupportReferenceLabel(args: {
  requestId?: string | null;
  runId?: string | null;
}): string | null {
  if (args.runId) {
    return `Run reference: ${args.runId}`;
  }

  if (args.requestId) {
    return `Support reference: ${args.requestId}`;
  }

  return null;
}

export function buildSupportMessage(
  baseMessage: string,
  args: {
    includeContact?: boolean;
    requestId?: string | null;
    runId?: string | null;
  },
): string {
  const parts = [baseMessage.trim()];
  const reference = getSupportReferenceLabel(args);

  if (reference) {
    parts.push(reference);
  }

  if (args.includeContact !== false) {
    parts.push('Contact help@framer.parts if you need support.');
  }

  return parts.filter(Boolean).join(' ');
}
